import { Button } from "@/components/ui/button";
import { ArrowLeft, Rocket, Loader2, DollarSign, Calendar } from "lucide-react";
import { Field, INPUT_CLS } from "./ChipInput";
import { formatCurrency, goalMath } from "./onboardingModel";

const QUICK_GOALS = [100000, 500000, 1000000, 5000000, 10000000];
const QUICK_DATES = [{ label: "6 months", days: 182 }, { label: "12 months", days: 365 }, { label: "End of year", eoy: true }];
const dateAfter = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().split("T")[0]; };
const endOfYear = () => `${new Date().getFullYear()}-12-31`;

const Chip = ({ on, onClick, children, testId }) => (
  <button onClick={onClick} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-ism-teal/20 border-ism-teal/40 text-ism-teal" : "border-white/10 text-gray-400 hover:border-white/20"}`} data-testid={testId}>{children}</button>
);

export const StepCommit = ({ data, patch, busy, onBack, onLaunch }) => {
  const math = goalMath(data.revenueGoal, data.averageDealSize, data.goalEndDate);
  const valid = Number(data.revenueGoal) >= 1000 && data.goalEndDate && new Date(data.goalEndDate) > new Date();

  return (
    <div className="space-y-6" data-testid="onboarding-step-commit">
      <div>
        <h2 className="text-2xl text-white font-light">Now the number.</h2>
        <p className="text-gray-400 mt-1 text-sm">With your buyer defined, a goal becomes a plan. I&apos;ll break it into wins, opportunities and prospects.</p>
      </div>

      <Field label="Revenue goal">
        <div className="relative">
          <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
          <input type="number" value={data.revenueGoal} onChange={(e) => patch({ revenueGoal: e.target.value })} className={`${INPUT_CLS} pl-12 text-2xl`} placeholder="1000000" autoFocus data-testid="onboarding-revenue-input" />
          {data.revenueGoal && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-ism-teal">{formatCurrency(data.revenueGoal)}</span>}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">{QUICK_GOALS.map((g) => <Chip key={g} on={Number(data.revenueGoal) === g} onClick={() => patch({ revenueGoal: String(g) })} testId={`goal-quick-${g}`}>{formatCurrency(g)}</Chip>)}</div>
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="By when">
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input type="date" value={data.goalEndDate} min={dateAfter(1)} onChange={(e) => patch({ goalEndDate: e.target.value })} className={`${INPUT_CLS} pl-12`} data-testid="onboarding-goaldate-input" />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">{QUICK_DATES.map((q) => <Chip key={q.label} on={false} onClick={() => patch({ goalEndDate: q.eoy ? endOfYear() : dateAfter(q.days) })} testId={`date-quick-${q.label.replace(/\s/g, "-")}`}>{q.label}</Chip>)}</div>
        </Field>
        <Field label="Average deal size" hint="Annual value of a typical win. A best guess is fine.">
          <div className="relative">
            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input type="number" value={data.averageDealSize} onChange={(e) => patch({ averageDealSize: e.target.value })} className={`${INPUT_CLS} pl-12`} placeholder="50000" data-testid="onboarding-deal-size-input" />
          </div>
        </Field>
      </div>

      <Field label="What does winning look like?" hint="Optional — your mission or the outcome behind the number. The agent uses it to prioritise.">
        <textarea value={data.mission || ""} onChange={(e) => patch({ mission: e.target.value })} rows={2} placeholder="e.g. Land 5 mid-market logos in healthcare so we can raise our Series A" className={`${INPUT_CLS} resize-none text-sm`} data-testid="onboarding-mission-input" />
      </Field>

      {math && (
        <div className="rounded-xl border border-ism-teal/20 bg-ism-teal/5 p-5" data-testid="goal-math">
          <p className="text-sm text-gray-300">
            At <span className="text-white">{formatCurrency(data.averageDealSize)}</span> per deal, <span className="text-white">{formatCurrency(data.revenueGoal)}</span>
            {math.days ? <> in <span className="text-white">{math.days} days</span></> : null} means roughly:
          </p>
          <div className="grid grid-cols-4 gap-3 mt-4 text-center">
            {[["wins", math.wins], ["proposals", math.proposals], ["opportunities", math.opportunities], ["prospects", math.prospects]].map(([l, v]) => (
              <div key={l}><p className="text-2xl text-ism-teal font-light">{v}</p><p className="text-xs text-gray-500">{l}</p></div>
            ))}
          </div>
          {math.winsPerMonth && <p className="text-xs text-gray-500 mt-3">≈ {math.winsPerMonth} wins a month. Sound right? You can tune conversion rates later in Goals.</p>}
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onBack} disabled={busy} className="text-gray-400 hover:text-white" data-testid="onboarding-back-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <Button onClick={onLaunch} disabled={!valid || busy} className="bg-ism-teal hover:bg-ism-teal/90 text-black font-medium rounded-xl px-6 h-11" data-testid="onboarding-launch-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Rocket className="h-4 w-4 mr-2" />} Launch my first hunt
        </Button>
      </div>
    </div>
  );
};
